import type { ReactNode } from 'react';

// Primitivas do sistema visual. Existem para que as telas parem de repetir
// dezenas de classes e divergirem entre si — que é a origem da sensação de
// "cada tela é de um app diferente".
//
// A regra do sistema: cor saturada é identidade de time. Botão, foco e estado
// são acromáticos, com uma única exceção semântica (perigo).

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2';

/**
 * Superfície invertida: tinta sobre papel no claro, papel sobre tinta no escuro.
 *
 * É o visual de "isto está selecionado / esta é a ação principal", e o par
 * `dark:` NÃO é opcional. Sem ele o elemento fica `pitch-950` sobre um `<body>`
 * que também é `pitch-950` — mesma cor, contraste 1,00:1, um botão que só
 * existe pelo rótulo flutuando. Metade das telas tinha isso porque copiou as
 * classes à mão em vez de usar `Button` ou esta constante.
 */
export const INVERTED = 'bg-pitch-950 text-chalk-50 dark:bg-chalk-100 dark:text-pitch-950';

type ButtonTone = 'solid' | 'outline' | 'ghost' | 'danger';

const BUTTON_TONE: Record<ButtonTone, string> = {
  // Preenchido com tinta, não com cor: a ação é importante, mas não é dado.
  solid:
    'bg-pitch-950 text-chalk-50 hover:bg-pitch-800 dark:bg-chalk-100 dark:text-pitch-950 dark:hover:bg-chalk-300',
  outline:
    'border border-chalk-300 hover:bg-chalk-100 dark:border-pitch-700 dark:hover:bg-pitch-800',
  ghost: 'hover:bg-chalk-100 dark:hover:bg-pitch-800',
  danger: 'text-signal-danger hover:bg-signal-danger/10',
};

// `lg` é o CTA que abre uma tela: sortear, continuar, criar conta. Existe
// porque quatro telas escreviam `px-5 py-3.5 text-base` à mão — e, junto com o
// tamanho, copiavam um `bg-pitch-950` sem par `dark:` que sumia no tema escuro.
const BUTTON_SIZE = {
  sm: 'min-h-11 px-3 py-1.5 text-sm',
  md: 'min-h-11 px-4 py-2.5 text-sm',
  lg: 'min-h-12 rounded-xl px-5 py-3.5 text-base',
} as const;

/**
 * As classes de um botão, sem o botão.
 *
 * Existe porque metade das ações primárias do produto são `<Link>`, não
 * `<button>` — aceitar convite, criar conta, ir para a pelada. Elas não podem
 * usar `<Button>`, e por isso vinham copiando as classes à mão; foi por aí que
 * o `bg-pitch-950` sem par `dark:` se espalhou. Agora link e botão leem a
 * aparência da mesma fonte.
 */
export function buttonClass({
  tone = 'solid',
  size = 'md',
  className = '',
}: { tone?: ButtonTone; size?: keyof typeof BUTTON_SIZE; className?: string } = {}) {
  return [
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all',
    // Resposta ao toque. O produto foi criticado por ser estático: um
    // controle que não reage ao ser pressionado parece uma imagem.
    'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100',
    BUTTON_SIZE[size],
    BUTTON_TONE[tone],
    FOCUS,
    className,
  ].join(' ');
}

export function Button({
  tone = 'solid',
  size = 'md',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: ButtonTone;
  size?: keyof typeof BUTTON_SIZE;
}) {
  return <button type="button" {...props} className={buttonClass({ tone, size, className })} />;
}

/**
 * O erro de uma escrita que falhou, junto do controle que a disparou.
 *
 * `role="alert"` porque quem não vê a tela precisa ser avisado sem navegar — e
 * porque o caso que isto existe para cobrir é justamente o da pessoa que achou
 * que tinha salvado.
 */
export function InlineError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-sm text-signal-danger">
      {children}
    </p>
  );
}

/**
 * Atalho de seleção: "Todos", "Mensalistas (12)".
 *
 * Sublinhado e sem moldura de propósito — fica ao lado de uma nuvem de nomes
 * que já são botões, e mais uma caixa ali viraria ruído em cima da lista que
 * importa.
 *
 * Mora aqui, e não na tela do sorteio onde nasceu, desde que a Agenda passou a
 * ter a mesma grade de nomes.
 */
export function QuickAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg px-2 py-1 text-xs font-semibold underline underline-offset-2 transition-colors hover:bg-chalk-100 focus-visible:outline-2 focus-visible:outline-offset-2 dark:hover:bg-pitch-800"
    >
      {children}
    </button>
  );
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={[
        'rounded-card border border-chalk-200 bg-white dark:border-pitch-700 dark:bg-pitch-900',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/**
 * Rótulo de seção. Pequeno, em caixa alta e espaçado — carrega a hierarquia
 * sem ocupar o peso de um título, que é o que faltava quando tudo tinha o
 * mesmo tamanho.
 */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <h2 className="eyebrow text-chalk-500 dark:text-chalk-400">{children}</h2>;
}

export function PageTitle({ children }: { children: ReactNode }) {
  return <h1 className="font-display text-display text-balance">{children}</h1>;
}

export function Field({
  label,
  hint,
  id,
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; id: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        {...props}
        className={[
          // `min-h-11`: campo de texto também é alvo de toque, e 42px de altura
          // não é 44 por dois pixels.
          'min-h-11 rounded-lg border border-chalk-300 bg-white px-3 py-2 text-sm transition-colors',
          'placeholder:text-chalk-400 dark:border-pitch-700 dark:bg-pitch-950',
          FOCUS,
          className,
        ].join(' ')}
      />
      {hint && <p className="text-xs text-chalk-500 dark:text-chalk-400">{hint}</p>}
    </div>
  );
}

/**
 * Uma faixa de abas.
 *
 * Existe porque três telas tinham três estilos diferentes de aba — pílula
 * preenchida no ranking, pílula menor no caixa, e uma terceira na pelada. Aba é
 * navegação: se muda de aparência a cada tela, deixa de ser reconhecível como
 * navegação.
 *
 * A cor da marca marca a posição atual. É a única cor do chrome, e nunca
 * encosta num time — não compete com o colete.
 */
export function Tabs<T extends string>({
  items,
  current,
  onChange,
  label,
  labelFor,
}: {
  items: readonly T[];
  current: T;
  onChange: (item: T) => void;
  label: string;
  labelFor: (item: T) => string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      // Rola na horizontal no celular em vez de quebrar em duas linhas: duas
      // linhas de aba empurram o conteúdo para baixo da dobra, e a navegação
      // vira mais uma seção da página.
      className="-mx-4 flex gap-1 overflow-x-auto border-b border-chalk-200 px-4 sm:mx-0 sm:px-0 dark:border-pitch-700"
    >
      {items.map((item) => {
        const active = current === item;
        return (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item)}
            className={[
              'relative shrink-0 px-3 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors',
              FOCUS,
              active
                ? 'text-pitch-950 dark:text-chalk-50'
                : 'text-chalk-500 hover:text-pitch-900 dark:text-chalk-400 dark:hover:text-chalk-200',
            ].join(' ')}
          >
            {labelFor(item)}
            {active && (
              <span
                aria-hidden
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-turf-600 dark:bg-turf-400"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Cores de colete por posição do time.
 *
 * A cor é o identificador real do time num pelada — "os laranjas", não "o time
 * 2". Por isso ela vem da posição, é estável dentro do sorteio, e nunca é
 * usada para outra coisa na interface.
 *
 * O nome do time acompanha sempre: cor sozinha não serve a quem não distingue
 * as duas primeiras.
 */
/**
 * Quantos coletes distintos o sistema sabe mostrar.
 *
 * É este número que limita quantos times a tela oferece — ver `MAX_TEAMS` no
 * core. Subiu de 4 para 6 em 30/07: com 30 pessoas em times de 6 são precisos
 * cinco times, e a tela travava em quatro. Passar do teto faria a cor repetir, e
 * dois times com o mesmo colete é pior do que um time a menos.
 */
export const BIB_COUNT = 6;

export function bibColor(index: number): string {
  return `var(--color-bib-${(index % BIB_COUNT) + 1})`;
}

/**
 * A cor de texto que se lê sobre `bibColor(index)`.
 *
 * Nunca escreva `text-white` sobre um colete: no amarelo isso mede 1,92:1. Cada
 * colete tem sua tinta medida e pareada em index.css.
 */
export function bibInk(index: number): string {
  return `var(--color-bib-${(index % BIB_COUNT) + 1}-ink)`;
}
